import { get, post, put, del } from '../../apiClient';
import { API_ENDPOINTS } from '../../config';

const opportunityService = {
  /**
   * Get all opportunities with filters and pagination
   * @param {Object} filters - Filter parameters
   * @param {Object} pagination - Pagination parameters
   * @returns {Promise<Object>} Opportunities data with pagination
   */
  getAllOpportunities: async (filters = {}, pagination = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.search) queryParams.append('search', filters.search);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.OPPORTUNITIES}?${queryParams.toString()}`
        : API_ENDPOINTS.CRM.OPPORTUNITIES;

      const response = await get(url);

      if (response && response.responseCode === 200) {
        const opportunities = Array.isArray(response.data)
          ? response.data
          : (response.data?.opportunities || []);

        return {
          success: true,
          opportunities: opportunities,
          pagination: response.data?.pagination || {}
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve opportunities',
          opportunities: []
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve opportunities',
        opportunities: []
      };
    }
  },

  /**
   * Get opportunity by ID
   * @param {String} id - Opportunity ID
   * @returns {Promise<Object>} Opportunity data
   */
  getOpportunityById: async (id) => {
    try {
      const response = await get(`${API_ENDPOINTS.CRM.OPPORTUNITIES}/${id}`);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          opportunity: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve opportunity',
          opportunity: null
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve opportunity',
        opportunity: null
      };
    }
  },

  /**
   * Update opportunity
   * @param {String} id - Opportunity ID
   * @param {Object} data - Updated opportunity data
   * @returns {Promise<Object>} Updated opportunity
   */
  updateOpportunity: async (id, data) => {
    try {
      const url = `${API_ENDPOINTS.CRM.OPPORTUNITIES}/${id}`;

      const response = await put(url, data);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          opportunity: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to update opportunity'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to update opportunity'
      };
    }
  },

  /**
   * Update opportunity stage
   * @param {String} id - Opportunity ID
   * @param {String} stage - New stage value
   * @returns {Promise<Object>} Updated opportunity
   */
  updateOpportunityStage: async (id, stage) => {
    try {
      const url = `${API_ENDPOINTS.CRM.OPPORTUNITIES}/${id}/stage`;

      const response = await put(url, { stage });

      if (response && response.responseCode === 200) {
        return {
          success: true,
          opportunity: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to update opportunity stage'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to update opportunity stage'
      };
    }
  },

  /**
   * Reopen a closed opportunity
   * @param {String} id - Opportunity ID
   * @param {String} stage - Target stage (either 'closed_won' or 'closed_lost')
   * @returns {Promise<Object>} Updated opportunity
   */
  reopenOpportunity: async (id, stage) => {
    try {
      const url = `${API_ENDPOINTS.CRM.OPPORTUNITIES}/${id}/reopen`;

      const response = await put(url, { stage });

      if (response && response.responseCode === 200) {
        return {
          success: true,
          opportunity: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to reopen opportunity'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to reopen opportunity'
      };
    }
  },

  /**
   * Delete opportunity
   * @param {String} id - Opportunity ID
   * @returns {Promise<Object>} Deletion result
   */
  deleteOpportunity: async (id) => {
    try {
      const url = `${API_ENDPOINTS.CRM.OPPORTUNITIES}/${id}`;

      const response = await del(url);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to delete opportunity'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to delete opportunity'
      };
    }
  },

  /**
   * Get converted opportunities (opportunities created from leads)
   * @param {Object} filters - Filter parameters (search, stage, page, limit)
   * @returns {Promise<Object>} Converted opportunities data with pagination
   */
  getConvertedOpportunities: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.search) queryParams.append('search', filters.search);
      if (filters.stage) queryParams.append('stage', filters.stage);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.OPPORTUNITIES}/converted?${queryParams.toString()}`
        : `${API_ENDPOINTS.CRM.OPPORTUNITIES}/converted`;

      const response = await get(url);

      if (response && response.responseCode === 200) {
        const opportunities = Array.isArray(response.data?.opportunities)
          ? response.data.opportunities
          : (Array.isArray(response.data) ? response.data : []);

        return {
          success: true,
          opportunities: opportunities,
          pagination: response.data?.pagination || {}
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve converted opportunities',
          opportunities: []
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve converted opportunities',
        opportunities: []
      };
    }
  },

  /**
   * Get stage counts for opportunities
   * @param {Object} filters - Filter parameters
   * @returns {Promise<Object>} Stage counts data
   */
  getStageCounts: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.search) queryParams.append('search', filters.search);
      if (filters.owner && filters.owner !== 'all') queryParams.append('owner', filters.owner);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.OPPORTUNITIES}/stage-counts?${queryParams.toString()}`
        : `${API_ENDPOINTS.CRM.OPPORTUNITIES}/stage-counts`;

      const response = await get(url);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          counts: response.data?.counts || response.data || {}
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve stage counts',
          counts: {}
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve stage counts',
        counts: {}
      };
    }
  },

  /**
   * Get opportunities with filters (for quote creation - qualified opportunities)
   */
  getOpportunities: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.search) queryParams.append('search', filters.search);
      if (filters.stage && filters.stage !== 'all') queryParams.append('stage', filters.stage);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.owner) queryParams.append('owner', filters.owner);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.OPPORTUNITIES}?${queryParams.toString()}`
        : API_ENDPOINTS.CRM.OPPORTUNITIES;

      const response = await get(url);

      if (response && response.responseCode === 200) {
        const opportunities = Array.isArray(response.data)
          ? response.data
          : (response.data?.opportunities || []);

        return {
          success: true,
          opportunities: opportunities,
          pagination: response.data?.pagination || {}
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve opportunities',
          opportunities: []
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve opportunities',
        opportunities: []
      };
    }
  },

  /**
   * Get single opportunity by ID
   */
  getOpportunityById: async (opportunityId) => {
    try {
      const response = await get(`${API_ENDPOINTS.CRM.OPPORTUNITIES}/${opportunityId}`);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          opportunity: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve opportunity',
          opportunity: null
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve opportunity',
        opportunity: null
      };
    }
  },

  /**
   * Update opportunity
   */
  updateOpportunity: async (opportunityId, opportunityData) => {
    try {
      const response = await put(`${API_ENDPOINTS.CRM.OPPORTUNITIES}/${opportunityId}`, opportunityData);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          opportunity: response.data?.opportunity || response.data,
          message: response.responseMessage || 'Opportunity updated successfully'
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to update opportunity',
          opportunity: null
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to update opportunity',
        opportunity: null
      };
    }
  },

  /**
   * Delete opportunity
   * @param {string} opportunityId - The ID of the opportunity to delete
   * @returns {Promise<Object>} Delete result
   */
  deleteOpportunity: async (opportunityId) => {
    try {
      const response = await del(`${API_ENDPOINTS.CRM.OPPORTUNITIES}/${opportunityId}`);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          message: response.responseMessage || 'Opportunity deleted successfully'
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to delete opportunity'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to delete opportunity'
      };
    }
  },

  /**
   * Get converted opportunities without existing quotations
   * @param {Object} filters - Filter parameters (search, stage, page, limit)
   * @returns {Promise<Object>} Converted opportunities without quotations data with pagination
   */
  getConvertedOpportunitiesWithoutQuotations: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.search) queryParams.append('search', filters.search);
      if (filters.stage) queryParams.append('stage', filters.stage);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.userId) queryParams.append('userId', filters.userId);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.OPPORTUNITIES_WITHOUT_QUOTATIONS}?${queryParams.toString()}`
        : API_ENDPOINTS.CRM.OPPORTUNITIES_WITHOUT_QUOTATIONS;

      const response = await get(url);

      if (response && response.responseCode === 200) {
        const opportunities = Array.isArray(response.data?.opportunities)
          ? response.data.opportunities
          : (Array.isArray(response.data) ? response.data : []);

        return {
          success: true,
          opportunities: opportunities,
          pagination: response.data?.pagination || {}
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve converted opportunities without quotations',
          opportunities: []
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve converted opportunities without quotations',
        opportunities: []
      };
    }
  },

  /**
   * Get all opportunities without existing quotations
   * @param {Object} filters - Filter parameters (search, stage, page, limit)
   * @returns {Promise<Object>} Opportunities without quotations data with pagination
   */
  getOpportunitiesWithoutQuotations: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.search) queryParams.append('search', filters.search);
      if (filters.stage) queryParams.append('stage', filters.stage);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.OPPORTUNITIES}/without-quotations?${queryParams.toString()}`
        : `${API_ENDPOINTS.CRM.OPPORTUNITIES}/without-quotations`;

      const response = await get(url);

      if (response && response.responseCode === 200) {
        const opportunities = Array.isArray(response.data?.opportunities)
          ? response.data.opportunities
          : (Array.isArray(response.data) ? response.data : []);

        return {
          success: true,
          opportunities: opportunities,
          pagination: response.data?.pagination || {}
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve opportunities without quotations',
          opportunities: []
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve opportunities without quotations',
        opportunities: []
      };
    }
  },

  /**
   * Get close won/lost KPIs (calculated from Opportunity model only)
   * @returns {Promise<Object>} KPI stats
   */
  getCloseWonLostKPIs: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.ownerId && filters.ownerId !== 'all') queryParams.append('ownerId', filters.ownerId);
      if (filters.owner && filters.owner !== 'all') queryParams.append('owner', filters.owner);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.OPPORTUNITIES}/close-won-lost/kpis?${queryParams.toString()}`
        : `${API_ENDPOINTS.CRM.OPPORTUNITIES}/close-won-lost/kpis`;

      const response = await get(url);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          stats: response.data || {
            totalOpportunities: 0,
            totalWonCount: 0,
            totalLostCount: 0,
            totalWonValue: 0,
            totalLostValue: 0
          }
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve close won/lost KPIs',
          stats: {
            totalOpportunities: 0,
            totalWonCount: 0,
            totalLostCount: 0,
            totalWonValue: 0,
            totalLostValue: 0
          }
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve close won/lost KPIs',
        stats: {
          totalOpportunities: 0,
          totalWonCount: 0,
          totalLostCount: 0,
          totalWonValue: 0,
          totalLostValue: 0
        }
      };
    }
  },

  /**
   * Get all opportunities list with filters
   * @param {Object} filters - Filter parameters (search, stage, page, limit)
   * @returns {Promise<Object>} All opportunities with pagination
   */
  getAllOpportunitiesList: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.search) queryParams.append('search', filters.search);
      if (filters.stage) queryParams.append('stage', filters.stage);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.OPPORTUNITIES}/all-list?${queryParams.toString()}`
        : `${API_ENDPOINTS.CRM.OPPORTUNITIES}/all-list`;

      const response = await get(url);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          opportunities: response.data?.opportunities || [],
          pagination: response.data?.pagination || {}
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve opportunities',
          opportunities: [],
          pagination: {}
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve opportunities',
        opportunities: [],
        pagination: {}
      };
    }
  },

  /**
   * Get close won/lost opportunities with stats
   * @param {Object} filters - Filter parameters (search, stage, page, limit)
   * @returns {Promise<Object>} Close won/lost opportunities with stats and pagination
   */
  getCloseWonLostOpportunities: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.search) queryParams.append('search', filters.search);
      if (filters.stage) queryParams.append('stage', filters.stage);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.ownerId && filters.ownerId !== 'all') queryParams.append('ownerId', filters.ownerId);
      if (filters.owner && filters.owner !== 'all') queryParams.append('owner', filters.owner);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.OPPORTUNITIES}/close-won-lost?${queryParams.toString()}`
        : `${API_ENDPOINTS.CRM.OPPORTUNITIES}/close-won-lost`;

      const response = await get(url);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          opportunities: response.data?.opportunities || [],
          pagination: response.data?.pagination || {},
          stats: response.data?.stats || {
            totalOpportunities: 0,
            totalWonCount: 0,
            totalLostCount: 0,
            totalWonValue: 0,
            totalLostValue: 0
          }
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve close won/lost opportunities',
          opportunities: [],
          stats: {
            totalOpportunities: 0,
            totalWonCount: 0,
            totalLostCount: 0,
            totalWonValue: 0,
            totalLostValue: 0
          }
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve close won/lost opportunities',
        opportunities: [],
        stats: {
          totalOpportunities: 0,
          totalWonCount: 0,
          totalLostCount: 0,
          totalWonValue: 0,
          totalLostValue: 0
        }
      };
    }
  },

  /**
   * Bulk delete opportunities
   * @param {Array<String>} ids - Array of opportunity IDs
   * @returns {Promise<Object>} Deletion result
   */
  bulkDeleteOpportunities: async (ids) => {
    try {
      const response = await post(`${API_ENDPOINTS.CRM.OPPORTUNITIES}/bulk-delete`, { ids });

      if (response && response.responseCode === 200) {
        return {
          success: true,
          message: response.responseMessage || 'Opportunities deleted successfully'
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to delete opportunities'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to delete opportunities'
      };
    }
  }
};

export default opportunityService;

